/** Строки загрузки: файл, который грузится сейчас, и файл из прошлого захода. */
import { useState, type ReactElement } from 'react';
import { formatBytes, formatDuration } from '../lib/video';
import type { ProjectAsset } from '../lib/types';
import type { UploadItem } from '../state/store';
import { t } from '../i18n';

export function UploadThumbnail({ src, video = false, photo = false, done = false }: {
  src?: string | null;
  video?: boolean;
  photo?: boolean;
  done?: boolean;
}): ReactElement {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const preview = src && src !== failedSrc;
  return (
    <div className="thumb" aria-hidden="true">
      {preview ? (
        video ? (
          <video src={`${src}#t=0.1`} muted playsInline preload="metadata" onError={() => setFailedSrc(src)} />
        ) : (
          <img src={src} alt="" decoding="async" onError={() => setFailedSrc(src)} />
        )
      ) : photo ? '🖼️' : '🎞️'}
      {done ? <span className="upload-check">✓</span> : null}
    </div>
  );
}

export function UploadRow({
  item,
  onRemove,
  onRetry,
}: {
  item: UploadItem;
  onRemove: () => void;
  onRetry: () => void;
}): ReactElement {
  const percent = Math.round(item.progress * 100);
  return (
    <div className="card">
      <div className="row">
        <UploadThumbnail done={item.status === 'done'} src={item.thumbUrl} video={item.kind !== 'photo'} photo={item.kind === 'photo'} />
        <div className="col grow">
          <span className="ellipsis">{item.name}</span>
          <span className="small muted">
            {formatBytes(item.bytes)}
            {item.durationS ? ` · ${formatDuration(item.durationS)}` : ''}
            {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
          </span>
        </div>
        <button type="button" className="btn small danger" onClick={onRemove}>
          {item.status === 'uploading' ? t('components.uploads.stop') : t('components.uploads.remove')}
        </button>
      </div>

      {item.status === 'checking' ? <span className="small muted">{t('components.uploads.checking')}</span> : null}

      {item.status === 'uploading' || item.status === 'queued' ? (
        <>
          <div className="bar">
            <span style={{ width: `${percent}%` }} />
          </div>
          <span className="small muted">
            {t('components.uploads.progress', { percent, done: formatBytes(item.uploadedBytes), total: formatBytes(item.bytes) })}
            {item.resumed ? t('components.uploads.resumed') : ''}
          </span>
        </>
      ) : null}

      {item.status === 'done' ? <span className="small" style={{ color: 'var(--ok)' }}>{t('components.uploads.done')}</span> : null}

      {item.status === 'canceled' ? (
        <div className="row between">
          <span className="small muted">{t('components.uploads.stopped')}</span>
          <button type="button" className="btn small" onClick={onRetry}>
            {t('components.uploads.resume')}
          </button>
        </div>
      ) : null}

      {item.status === 'error' && item.error ? (
        <div className="col">
          <div className="banner error">{item.error}</div>
          <button type="button" className="btn small" onClick={onRetry}>
            {t('components.uploads.retry')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Файл, загруженный в прошлый заход: черновик открыли заново. */
export function AssetRow({ asset, onRemove }: { asset: ProjectAsset; onRemove: () => void }): ReactElement {
  return (
    <div className="card">
      <div className="row">
        <UploadThumbnail done={asset.uploaded !== false} src={asset.thumbUrl} photo={asset.kind === 'photo'} />
        <div className="col grow">
          <span className="ellipsis">{asset.filename ?? t('components.uploads.unnamed')}</span>
          <span className="small muted">
            {typeof asset.bytes === 'number' ? formatBytes(asset.bytes) : t('components.uploads.earlier')}
            {asset.durationS ? ` · ${formatDuration(asset.durationS)}` : ''}
          </span>
        </div>
        <button type="button" className="btn small danger" onClick={onRemove}>
          {t('components.uploads.remove')}
        </button>
      </div>
      <span className="small" style={{ color: asset.uploaded === false ? 'var(--muted)' : 'var(--ok)' }}>{asset.uploaded === false ? t('components.uploads.incomplete') : t('components.uploads.uploaded')}</span>
    </div>
  );
}

/** Область выбора файла: главная (видео) — светлая плитка, второстепенная — пунктирная карточка. */
export function Dropzone({
  title,
  hint,
  primary,
  disabled,
  onClick,
}: {
  title: string;
  hint: string;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      className={primary ? 'dropzone primary' : 'dropzone'}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="col grow">
        <strong>{title}</strong>
        <span className="small muted">{hint}</span>
      </span>
      <span className="plus" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </span>
    </button>
  );
}
